/**
 * Zoom Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Zoom',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content:
        'This guide will walk you through setting up and using Sokuji with Zoom for real-time language translation during your meetings.',
    },
    steps: [
      {
        title: 'Open Zoom Meeting Link',
        content:
          'Open your Zoom meeting link in Chrome or Edge browser. The browser will attempt to open the Zoom desktop application.',
        screenshot: '1.png',
        tip: 'Make sure you have the Sokuji extension installed and enabled in your browser.',
      },
      {
        title: 'Decline Native App',
        content:
          'When prompted to open the Zoom desktop application, click <strong>Cancel</strong> or close the dialog box to stay in the browser.',
        screenshot: '2.png',
        tip: 'This is important - you must use the browser version of Zoom for Sokuji to work.',
      },
      {
        title: 'Join from Browser',
        content:
          'After declining the desktop app, look for the <strong>Join from your browser</strong> link that appears on the page and click it.',
        screenshot: '3.png',
        tip: 'This link may appear at the bottom of the page or in a notification message.',
      },
      {
        title: 'Open Sokuji Sidebar',
        content:
          'Click the Sokuji extension icon in the top-right corner of your browser, then click to open the Sokuji sidebar panel.',
        screenshot: '4.png',
        tip: 'The sidebar will open on the right side of your browser window.',
      },
      {
        title: 'Configure Sokuji Settings',
        content:
          'In the Sokuji sidebar, configure your translation settings including source and target languages, AI provider, and other preferences.',
        screenshot: '5.png',
        tip: 'Make sure to test your microphone and select the appropriate input device.',
      },
      {
        title: 'Select Virtual Microphone in Zoom',
        content:
          "In Zoom's audio settings, click the microphone dropdown and select <strong>Sokuji Virtual Microphone</strong> as your input device.",
        screenshot: '6.png',
        tip: 'You can access audio settings by clicking the arrow next to the microphone icon in Zoom.',
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Speak clearly and avoid background noise',
        'Pause briefly between sentences for better translation accuracy',
        'Test your audio before important meetings',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: "Why can't I see the Sokuji Virtual Microphone?",
          answer:
            "Make sure the Sokuji extension is installed and active. Try refreshing the Zoom page. If the issue persists, check that you've granted microphone permissions to the extension.",
        },
        {
          question: 'Can other participants hear both languages?',
          answer:
            "By default, participants only hear the translated audio. If you turn on Passthrough in Sokuji's Speech block, participants hear your original voice under the translation at the same time. This is useful when you want listeners to hear both languages for context or verification.",
        },
        {
          question: 'Does Sokuji work with Zoom recordings?',
          answer:
            "Yes, the translated audio will be included in the meeting recording if you're using Sokuji Virtual Microphone.",
        },
        {
          question: 'What is Passthrough?',
          answer:
            'Passthrough mixes your own voice, at a lower volume, under the translation into the virtual microphone, so listeners hear both languages at once. It is a sub-row of Translation the other side hears in the Speech block (Quick settings, or Settings → Advanced → Audio), with a volume slider: 20% by default, 60% at most. It is greyed out while Translation the other side hears is off. This is particularly useful for bilingual audiences or when you want to keep the emotional tone and nuance of your original speech.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content:
        'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Zoom 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Zoom 中设置和使用 Sokuji，实现会议中的实时语言翻译。',
    },
    steps: [
      {
        title: '打开 Zoom 会议链接',
        content:
          '在 Chrome 或 Edge 浏览器中打开您的 Zoom 会议链接。浏览器会尝试打开 Zoom 桌面应用程序。',
        screenshot: '1.png',
        tip: '确保您已在浏览器中安装并启用了 Sokuji 扩展。',
      },
      {
        title: '拒绝打开原生应用',
        content:
          '当提示打开 Zoom 桌面应用程序时，点击<strong>取消</strong>或关闭对话框以留在浏览器中。',
        screenshot: '2.png',
        tip: '这很重要 - 您必须使用浏览器版本的 Zoom 才能使 Sokuji 工作。',
      },
      {
        title: '从浏览器加入',
        content: '拒绝桌面应用后，在页面上查找<strong>从您的浏览器加入</strong>链接并点击它。',
        screenshot: '3.png',
        tip: '此链接可能出现在页面底部或通知消息中。',
      },
      {
        title: '打开 Sokuji 侧边栏',
        content: '点击浏览器右上角的 Sokuji 扩展图标，然后点击打开 Sokuji 侧边栏面板。',
        screenshot: '4.png',
        tip: '侧边栏将在浏览器窗口的右侧打开。',
      },
      {
        title: '配置 Sokuji 设置',
        content:
          '在 Sokuji 侧边栏中，配置您的翻译设置，包括源语言和目标语言、AI 提供商和其他偏好设置。',
        screenshot: '5.png',
        tip: '确保测试您的麦克风并选择适当的输入设备。',
      },
      {
        title: '在 Zoom 中选择虚拟麦克风',
        content:
          '在 Zoom 的音频设置中，点击麦克风下拉菜单，选择 <strong>Sokuji Virtual Microphone</strong> 作为您的输入设备。',
        screenshot: '6.png',
        tip: '您可以通过点击 Zoom 中麦克风图标旁边的箭头来访问音频设置。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '清晰说话，避免背景噪音',
        '句子之间稍作停顿以提高翻译准确性',
        '重要会议前测试音频',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: '为什么看不到 Sokuji 虚拟麦克风？',
          answer:
            '确保 Sokuji 扩展已安装并处于活动状态。尝试刷新 Zoom 页面。如果问题仍然存在，请检查是否已授予扩展麦克风权限。',
        },
        {
          question: '其他参与者能听到两种语言吗？',
          answer:
            '默认情况下，参与者只能听到翻译后的音频。如果您在 Sokuji 的语音区块中打开“原声直通”，参与者会同时听到译音和垫在下面的您的原声。当您希望听众听到两种语言以便理解上下文或进行验证时，此功能非常有用。',
        },
        {
          question: 'Sokuji 支持 Zoom 录制吗？',
          answer: '是的，如果您使用 Sokuji 虚拟麦克风，翻译后的音频将包含在会议录制中。',
        },
        {
          question: '什么是原声直通？',
          answer:
            '原声直通会把您的原声以较低音量混在译音下面，一起送进虚拟麦克风，让听众同时听到两种语言。它是语音区块（快捷设置，或设置 → 高级 → 音频）中“对方听到的翻译”下的子项，带一个音量滑块：默认 20%，最大 60%。“对方听到的翻译”关闭时，它会变灰。这对于双语听众或当您想要保持原始语音的情感语调和细微差别时特别有用。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content:
        '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Zoom で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content:
        'このガイドでは、Zoom で Sokuji を設定して使用し、会議中にリアルタイム言語翻訳を行う方法を説明します。',
    },
    steps: [
      {
        title: 'Zoom ミーティングリンクを開く',
        content:
          'Chrome または Edge ブラウザで Zoom ミーティングリンクを開きます。ブラウザは Zoom デスクトップアプリケーションを開こうとします。',
        screenshot: '1.png',
        tip: 'ブラウザに Sokuji 拡張機能がインストールされ、有効になっていることを確認してください。',
      },
      {
        title: 'ネイティブアプリを拒否',
        content:
          'Zoom デスクトップアプリケーションを開くように求められたら、<strong>キャンセル</strong>をクリックするか、ダイアログボックスを閉じてブラウザに留まります。',
        screenshot: '2.png',
        tip: 'これは重要です - Sokuji を動作させるには、ブラウザ版の Zoom を使用する必要があります。',
      },
      {
        title: 'ブラウザから参加',
        content:
          'デスクトップアプリを拒否した後、ページに表示される<strong>ブラウザから参加してください</strong>リンクを探してクリックします。',
        screenshot: '3.png',
        tip: 'このリンクはページの下部または通知メッセージに表示される場合があります。',
      },
      {
        title: 'Sokuji サイドバーを開く',
        content:
          'ブラウザの右上隅にある Sokuji 拡張機能アイコンをクリックし、Sokuji サイドバーパネルを開きます。',
        screenshot: '4.png',
        tip: 'サイドバーはブラウザウィンドウの右側に開きます。',
      },
      {
        title: 'Sokuji 設定を構成',
        content:
          'Sokuji サイドバーで、ソース言語とターゲット言語、AI プロバイダー、その他の設定を含む翻訳設定を構成します。',
        screenshot: '5.png',
        tip: 'マイクをテストして、適切な入力デバイスを選択してください。',
      },
      {
        title: 'Zoom で仮想マイクを選択',
        content:
          'Zoom のオーディオ設定で、マイクのドロップダウンをクリックし、入力デバイスとして <strong>Sokuji Virtual Microphone</strong> を選択します。',
        screenshot: '6.png',
        tip: 'Zoom のマイクアイコンの横にある矢印をクリックして、オーディオ設定にアクセスできます。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        'はっきりと話し、背景ノイズを避ける',
        '翻訳精度を向上させるため、文の間で少し一時停止する',
        '重要な会議の前にオーディオをテストする',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Sokuji 仮想マイクが表示されないのはなぜですか？',
          answer:
            'Sokuji 拡張機能がインストールされ、アクティブであることを確認してください。Zoom ページを更新してみてください。問題が解決しない場合は、拡張機能にマイクの権限を付与したかどうかを確認してください。',
        },
        {
          question: '他の参加者は両方の言語を聞くことができますか？',
          answer:
            'デフォルトでは、参加者は翻訳された音声のみを聞きます。Sokuji の「音声」ブロックで「原音パススルー」をオンにすると、参加者には翻訳の下にあなたの元の声も同時に聞こえます。この機能は、リスナーにコンテキストの理解や検証のために両方の言語を聞いてもらいたい場合に便利です。',
        },
        {
          question: 'Sokuji は Zoom の録画で動作しますか？',
          answer:
            'はい、Sokuji 仮想マイクを使用している場合、翻訳された音声が会議の録画に含まれます。',
        },
        {
          question: '原音パススルーとは何ですか？',
          answer:
            '原音パススルーは、あなた自身の声を小さめの音量で翻訳音声の下に混ぜて仮想マイクに送り、リスナーが両方の言語を同時に聞けるようにする機能です。「音声」ブロック（設定の「クイック」、または 設定 → 詳細 → オーディオ）の「相手に聞こえる翻訳」の下にある項目で、音量スライダーがあります（デフォルト 20%、最大 60%）。「相手に聞こえる翻訳」がオフの間はグレー表示になります。これは、バイリンガルの聴衆や、元の話し声の感情的なトーンやニュアンスを維持したい場合に特に便利です。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content:
        '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Zoom에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content:
        '이 가이드는 Zoom에서 Sokuji를 설정하고 사용하여 회의 중 실시간 언어 번역을 수행하는 방법을 안내합니다.',
    },
    steps: [
      {
        title: 'Zoom 회의 링크 열기',
        content:
          'Chrome 또는 Edge 브라우저에서 Zoom 회의 링크를 엽니다. 브라우저가 Zoom 데스크톱 애플리케이션을 열려고 시도합니다.',
        screenshot: '1.png',
        tip: '브라우저에 Sokuji 확장 프로그램이 설치되고 활성화되어 있는지 확인하세요.',
      },
      {
        title: '네이티브 앱 거부',
        content:
          'Zoom 데스크톱 애플리케이션을 열라는 메시지가 표시되면 <strong>취소</strong>를 클릭하거나 대화 상자를 닫아 브라우저에 남아있으세요.',
        screenshot: '2.png',
        tip: '이것은 중요합니다 - Sokuji가 작동하려면 브라우저 버전의 Zoom을 사용해야 합니다.',
      },
      {
        title: '브라우저에서 참여',
        content:
          '데스크톱 앱을 거부한 후 페이지에 나타나는 <strong>브라우저에서 참가</strong> 링크를 찾아 클릭하세요.',
        screenshot: '3.png',
        tip: '이 링크는 페이지 하단이나 알림 메시지에 나타날 수 있습니다.',
      },
      {
        title: 'Sokuji 사이드바 열기',
        content:
          '브라우저 오른쪽 상단의 Sokuji 확장 프로그램 아이콘을 클릭한 다음 Sokuji 사이드바 패널을 엽니다.',
        screenshot: '4.png',
        tip: '사이드바는 브라우저 창의 오른쪽에 열립니다.',
      },
      {
        title: 'Sokuji 설정 구성',
        content:
          'Sokuji 사이드바에서 소스 언어와 대상 언어, AI 제공업체 및 기타 환경 설정을 포함한 번역 설정을 구성하세요.',
        screenshot: '5.png',
        tip: '마이크를 테스트하고 적절한 입력 장치를 선택하세요.',
      },
      {
        title: 'Zoom에서 가상 마이크 선택',
        content:
          'Zoom의 오디오 설정에서 마이크 드롭다운을 클릭하고 입력 장치로 <strong>Sokuji Virtual Microphone</strong>을 선택하세요.',
        screenshot: '6.png',
        tip: 'Zoom의 마이크 아이콘 옆 화살표를 클릭하여 오디오 설정에 액세스할 수 있습니다.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '명확하게 말하고 배경 소음을 피하세요',
        '번역 정확도 향상을 위해 문장 사이에 잠시 멈추세요',
        '중요한 회의 전에 오디오를 테스트하세요',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Sokuji 가상 마이크가 보이지 않는 이유는 무엇인가요?',
          answer:
            'Sokuji 확장 프로그램이 설치되고 활성화되어 있는지 확인하세요. Zoom 페이지를 새로 고쳐 보세요. 문제가 지속되면 확장 프로그램에 마이크 권한을 부여했는지 확인하세요.',
        },
        {
          question: '다른 참가자들이 두 언어를 모두 들을 수 있나요?',
          answer:
            '기본적으로 참가자들은 번역된 오디오만 듣습니다. Sokuji의 \'음성\' 블록에서 \'원음 패스스루\'를 켜면 참가자들이 번역 아래에 깔린 당신의 원래 목소리도 함께 듣습니다. 이 기능은 청취자가 맥락 이해나 검증을 위해 두 언어를 모두 들어야 할 때 유용합니다.',
        },
        {
          question: 'Sokuji가 Zoom 녹화에서 작동하나요?',
          answer: '네, Sokuji 가상 마이크를 사용하는 경우 번역된 오디오가 회의 녹화에 포함됩니다.',
        },
        {
          question: '원음 패스스루란 무엇인가요?',
          answer:
            '원음 패스스루는 당신의 목소리를 낮은 음량으로 번역 음성 아래에 섞어 가상 마이크로 보내, 청취자가 두 언어를 동시에 들을 수 있게 하는 기능입니다. \'음성\' 블록(설정의 \'빠른\' 화면, 또는 설정 → 고급 → 오디오)에서 \'상대방에게 들리는 번역\' 아래의 하위 항목이며, 음량 슬라이더가 있습니다(기본값 20%, 최대 60%). \'상대방에게 들리는 번역\'이 꺼져 있는 동안에는 회색으로 비활성화됩니다. 이것은 이중 언어 청중이나 원래 말소리의 감정적인 톤과 뉘앙스를 유지하고 싶을 때 특히 유용합니다.',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content:
        '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Zoom',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripci\u00f3n general',
      content:
        'Esta gu\u00eda le explicar\u00e1 c\u00f3mo configurar y usar Sokuji con Zoom para la traducci\u00f3n de idiomas en tiempo real durante sus reuniones.',
    },
    steps: [
      {
        title: 'Abra el enlace de la reuni\u00f3n de Zoom',
        content:
          'Abra el enlace de su reuni\u00f3n de Zoom en el navegador Chrome o Edge. El navegador intentar\u00e1 abrir la aplicaci\u00f3n de escritorio de Zoom.',
        screenshot: '1.png',
        tip: 'Aseg\u00farese de tener la extensi\u00f3n Sokuji instalada y habilitada en su navegador.',
      },
      {
        title: 'Rechace la aplicaci\u00f3n nativa',
        content:
          'Cuando se le solicite abrir la aplicaci\u00f3n de escritorio de Zoom, haga clic en <strong>Cancelar</strong> o cierre el cuadro de di\u00e1logo para permanecer en el navegador.',
        screenshot: '2.png',
        tip: 'Esto es importante: debe usar la versi\u00f3n del navegador de Zoom para que Sokuji funcione.',
      },
      {
        title: 'Unirse desde el navegador',
        content:
          'Despu\u00e9s de rechazar la aplicaci\u00f3n de escritorio, busque el enlace <strong>Unirse desde su navegador</strong> que aparece en la p\u00e1gina y haga clic en \u00e9l.',
        screenshot: '3.png',
        tip: 'Este enlace puede aparecer en la parte inferior de la p\u00e1gina o en un mensaje de notificaci\u00f3n.',
      },
      {
        title: 'Abra la barra lateral de Sokuji',
        content:
          'Haga clic en el icono de la extensi\u00f3n Sokuji en la esquina superior derecha de su navegador, luego haga clic para abrir el panel lateral de Sokuji.',
        screenshot: '4.png',
        tip: 'La barra lateral se abrir\u00e1 en el lado derecho de la ventana de su navegador.',
      },
      {
        title: 'Configure los ajustes de Sokuji',
        content:
          'En la barra lateral de Sokuji, configure sus ajustes de traducci\u00f3n, incluyendo los idiomas de origen y destino, el proveedor de IA y otras preferencias.',
        screenshot: '5.png',
        tip: 'Aseg\u00farese de probar su micr\u00f3fono y seleccionar el dispositivo de entrada adecuado.',
      },
      {
        title: 'Seleccione el micr\u00f3fono virtual en Zoom',
        content:
          'En los ajustes de audio de Zoom, haga clic en el men\u00fa desplegable del micr\u00f3fono y seleccione <strong>Sokuji Virtual Microphone</strong> como su dispositivo de entrada.',
        screenshot: '6.png',
        tip: 'Puede acceder a los ajustes de audio haciendo clic en la flecha junto al icono del micr\u00f3fono en Zoom.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Hable con claridad y evite el ruido de fondo',
        'Haga una breve pausa entre oraciones para mejorar la precisi\u00f3n de la traducci\u00f3n',
        'Pruebe su audio antes de reuniones importantes',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '\u00bfPor qu\u00e9 no puedo ver Sokuji Virtual Microphone?',
          answer:
            'Aseg\u00farese de que la extensi\u00f3n Sokuji est\u00e9 instalada y activa. Intente actualizar la p\u00e1gina de Zoom. Si el problema persiste, verifique que haya otorgado permisos de micr\u00f3fono a la extensi\u00f3n.',
        },
        {
          question: '\u00bfPueden los dem\u00e1s participantes escuchar ambos idiomas?',
          answer:
            'De forma predeterminada, los participantes solo escuchan el audio traducido. Si activa "Paso directo" en el bloque "Voz" de Sokuji, los participantes escucharán su voz original por debajo de la traducción al mismo tiempo. Esta función es útil cuando desea que los oyentes escuchen ambos idiomas para contexto o verificación.',
        },
        {
          question: '\u00bfFunciona Sokuji con las grabaciones de Zoom?',
          answer:
            'S\u00ed, el audio traducido se incluir\u00e1 en la grabaci\u00f3n de la reuni\u00f3n si est\u00e1 usando Sokuji Virtual Microphone.',
        },
        {
          question: '¿Qué es "Paso directo"?',
          answer:
            '"Paso directo" mezcla su propia voz, a menor volumen, por debajo de la traducción en el micrófono virtual, para que los oyentes escuchen ambos idiomas a la vez. Es una subfila de "Traducción que oye la otra parte" en el bloque "Voz" (ajustes "Rápido", o Configuración → Avanzado → Audio), con un control de volumen: 20 % por defecto, 60 % como máximo. Aparece atenuado mientras "Traducción que oye la otra parte" está desactivada. Esto es particularmente útil para audiencias bilingües o cuando desea mantener el tono emocional y los matices de su discurso original.',
        },
      ],
    },
    troubleshooting: {
      title: 'Soluci\u00f3n de problemas',
      content:
        'Si encuentra alg\u00fan problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o cont\u00e1ctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Zoom',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aper\u00e7u',
      content:
        'Ce guide vous expliquera comment configurer et utiliser Sokuji avec Zoom pour la traduction linguistique en temps r\u00e9el lors de vos r\u00e9unions.',
    },
    steps: [
      {
        title: 'Ouvrez le lien de la r\u00e9union Zoom',
        content:
          'Ouvrez votre lien de r\u00e9union Zoom dans le navigateur Chrome ou Edge. Le navigateur tentera d\u2019ouvrir l\u2019application de bureau Zoom.',
        screenshot: '1.png',
        tip: 'Assurez-vous que l\u2019extension Sokuji est install\u00e9e et activ\u00e9e dans votre navigateur.',
      },
      {
        title: 'Refusez l\u2019application native',
        content:
          'Lorsque vous \u00eates invit\u00e9 \u00e0 ouvrir l\u2019application de bureau Zoom, cliquez sur <strong>Annuler</strong> ou fermez la bo\u00eete de dialogue pour rester dans le navigateur.',
        screenshot: '2.png',
        tip: 'C\u2019est important \u2013 vous devez utiliser la version navigateur de Zoom pour que Sokuji fonctionne.',
      },
      {
        title: 'Rejoindre depuis le navigateur',
        content:
          'Apr\u00e8s avoir refus\u00e9 l\u2019application de bureau, recherchez le lien <strong>Rejoindre depuis votre navigateur</strong> qui appara\u00eet sur la page et cliquez dessus.',
        screenshot: '3.png',
        tip: 'Ce lien peut appara\u00eetre en bas de la page ou dans un message de notification.',
      },
      {
        title: 'Ouvrez la barre lat\u00e9rale Sokuji',
        content:
          'Cliquez sur l\u2019ic\u00f4ne de l\u2019extension Sokuji dans le coin sup\u00e9rieur droit de votre navigateur, puis cliquez pour ouvrir le panneau lat\u00e9ral Sokuji.',
        screenshot: '4.png',
        tip: 'La barre lat\u00e9rale s\u2019ouvrira sur le c\u00f4t\u00e9 droit de la fen\u00eatre de votre navigateur.',
      },
      {
        title: 'Configurez les param\u00e8tres de Sokuji',
        content:
          'Dans la barre lat\u00e9rale de Sokuji, configurez vos param\u00e8tres de traduction, y compris les langues source et cible, le fournisseur d\u2019IA et d\u2019autres pr\u00e9f\u00e9rences.',
        screenshot: '5.png',
        tip: 'Assurez-vous de tester votre microphone et de s\u00e9lectionner le p\u00e9riph\u00e9rique d\u2019entr\u00e9e appropri\u00e9.',
      },
      {
        title: 'S\u00e9lectionnez le microphone virtuel dans Zoom',
        content:
          'Dans les param\u00e8tres audio de Zoom, cliquez sur le menu d\u00e9roulant du microphone et s\u00e9lectionnez <strong>Sokuji Virtual Microphone</strong> comme p\u00e9riph\u00e9rique d\u2019entr\u00e9e.',
        screenshot: '6.png',
        tip: 'Vous pouvez acc\u00e9der aux param\u00e8tres audio en cliquant sur la fl\u00e8che \u00e0 c\u00f4t\u00e9 de l\u2019ic\u00f4ne du microphone dans Zoom.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs r\u00e9sultats',
      items: [
        'Parlez clairement et \u00e9vitez le bruit de fond',
        'Faites une br\u00e8ve pause entre les phrases pour une meilleure pr\u00e9cision de traduction',
        'Testez votre audio avant les r\u00e9unions importantes',
      ],
    },
    faq: {
      title: 'Questions fr\u00e9quemment pos\u00e9es',
      items: [
        {
          question: 'Pourquoi ne puis-je pas voir Sokuji Virtual Microphone\u00a0?',
          answer:
            'Assurez-vous que l\u2019extension Sokuji est install\u00e9e et active. Essayez d\u2019actualiser la page Zoom. Si le probl\u00e8me persiste, v\u00e9rifiez que vous avez accord\u00e9 les autorisations de microphone \u00e0 l\u2019extension.',
        },
        {
          question: 'Les autres participants peuvent-ils entendre les deux langues\u00a0?',
          answer:
            'Par défaut, les participants n’entendent que l’audio traduit. Si vous activez « Passage direct » dans le bloc « Voix » de Sokuji, les participants entendent votre voix originale sous la traduction, en même temps. Cette fonctionnalité est utile lorsque vous souhaitez que les auditeurs entendent les deux langues pour le contexte ou la vérification.',
        },
        {
          question: 'Sokuji fonctionne-t-il avec les enregistrements Zoom\u00a0?',
          answer:
            'Oui, l\u2019audio traduit sera inclus dans l\u2019enregistrement de la r\u00e9union si vous utilisez Sokuji Virtual Microphone.',
        },
        {
          question: 'Qu’est-ce que le « Passage direct » ?',
          answer:
            'Le « Passage direct » mélange votre propre voix, à un volume plus bas, sous la traduction dans le micro virtuel, pour que les auditeurs entendent les deux langues à la fois. C’est une sous-ligne de « Traduction entendue par l\'autre » dans le bloc « Voix » (paramètres « Rapide », ou Paramètres → Avancé → Audio), avec un curseur de volume : 20 % par défaut, 60 % au maximum. Il est grisé tant que « Traduction entendue par l\'autre » est désactivée. C’est particulièrement utile pour les audiences bilingues ou lorsque vous souhaitez conserver le ton émotionnel et les nuances de votre discours original.',
        },
      ],
    },
    troubleshooting: {
      title: 'D\u00e9pannage',
      content:
        'Si vous rencontrez des probl\u00e8mes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">d\u00e9p\u00f4t GitHub</a> ou contactez-nous \u00e0 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\u2019aide.',
    },
  },
  ar: {
    pageTitle: '\u0627\u0633\u062a\u062e\u062f\u0627\u0645 Sokuji \u0645\u0639 Zoom',
    backLink: '\u0627\u0644\u0639\u0648\u062f\u0629 \u0625\u0644\u0649 \u0627\u0644\u0645\u0648\u0627\u0642\u0639 \u0627\u0644\u0645\u062f\u0639\u0648\u0645\u0629',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629',
      content:
        '\u0633\u064a\u0631\u0634\u062f\u0643 \u0647\u0630\u0627 \u0627\u0644\u062f\u0644\u064a\u0644 \u062e\u0644\u0627\u0644 \u0625\u0639\u062f\u0627\u062f \u0648\u0627\u0633\u062a\u062e\u062f\u0627\u0645 Sokuji \u0645\u0639 Zoom \u0644\u0644\u062a\u0631\u062c\u0645\u0629 \u0627\u0644\u0641\u0648\u0631\u064a\u0629 \u0623\u062b\u0646\u0627\u0621 \u0627\u062c\u062a\u0645\u0627\u0639\u0627\u062a\u0643.',
    },
    steps: [
      {
        title: '\u0627\u0641\u062a\u062d \u0631\u0627\u0628\u0637 \u0627\u062c\u062a\u0645\u0627\u0639 Zoom',
        content:
          '\u0627\u0641\u062a\u062d \u0631\u0627\u0628\u0637 \u0627\u062c\u062a\u0645\u0627\u0639 Zoom \u0641\u064a \u0645\u062a\u0635\u0641\u062d Chrome \u0623\u0648 Edge. \u0633\u064a\u062d\u0627\u0648\u0644 \u0627\u0644\u0645\u062a\u0635\u0641\u062d \u0641\u062a\u062d \u062a\u0637\u0628\u064a\u0642 Zoom \u0644\u0633\u0637\u062d \u0627\u0644\u0645\u0643\u062a\u0628.',
        screenshot: '1.png',
        tip: '\u062a\u0623\u0643\u062f \u0645\u0646 \u0623\u0646 \u0625\u0636\u0627\u0641\u0629 Sokuji \u0645\u062b\u0628\u062a\u0629 \u0648\u0645\u0641\u0639\u0644\u0629 \u0641\u064a \u0645\u062a\u0635\u0641\u062d\u0643.',
      },
      {
        title: '\u0627\u0631\u0641\u0636 \u0627\u0644\u062a\u0637\u0628\u064a\u0642 \u0627\u0644\u0623\u0635\u0644\u064a',
        content:
          '\u0639\u0646\u062f\u0645\u0627 \u064a\u064f\u0637\u0644\u0628 \u0645\u0646\u0643 \u0641\u062a\u062d \u062a\u0637\u0628\u064a\u0642 Zoom \u0644\u0633\u0637\u062d \u0627\u0644\u0645\u0643\u062a\u0628\u060c \u0627\u0646\u0642\u0631 \u0639\u0644\u0649 <strong>\u0625\u0644\u063a\u0627\u0621</strong> \u0623\u0648 \u0623\u063a\u0644\u0642 \u0645\u0631\u0628\u0639 \u0627\u0644\u062d\u0648\u0627\u0631 \u0644\u0644\u0628\u0642\u0627\u0621 \u0641\u064a \u0627\u0644\u0645\u062a\u0635\u0641\u062d.',
        screenshot: '2.png',
        tip: '\u0647\u0630\u0627 \u0645\u0647\u0645 - \u064a\u062c\u0628 \u0627\u0633\u062a\u062e\u062f\u0627\u0645 \u0625\u0635\u062f\u0627\u0631 \u0627\u0644\u0645\u062a\u0635\u0641\u062d \u0645\u0646 Zoom \u0644\u0643\u064a \u064a\u0639\u0645\u0644 Sokuji.',
      },
      {
        title: '\u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645 \u0645\u0646 \u0627\u0644\u0645\u062a\u0635\u0641\u062d',
        content:
          '\u0628\u0639\u062f \u0631\u0641\u0636 \u062a\u0637\u0628\u064a\u0642 \u0633\u0637\u062d \u0627\u0644\u0645\u0643\u062a\u0628\u060c \u0627\u0628\u062d\u062b \u0639\u0646 \u0631\u0627\u0628\u0637 <strong>\u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645 \u0645\u0646 \u0645\u062a\u0635\u0641\u062d\u0643</strong> \u0627\u0644\u0630\u064a \u064a\u0638\u0647\u0631 \u0639\u0644\u0649 \u0627\u0644\u0635\u0641\u062d\u0629 \u0648\u0627\u0646\u0642\u0631 \u0639\u0644\u064a\u0647.',
        screenshot: '3.png',
        tip: '\u0642\u062f \u064a\u0638\u0647\u0631 \u0647\u0630\u0627 \u0627\u0644\u0631\u0627\u0628\u0637 \u0641\u064a \u0623\u0633\u0641\u0644 \u0627\u0644\u0635\u0641\u062d\u0629 \u0623\u0648 \u0641\u064a \u0631\u0633\u0627\u0644\u0629 \u0625\u0634\u0639\u0627\u0631.',
      },
      {
        title: '\u0627\u0641\u062a\u062d \u0627\u0644\u0634\u0631\u064a\u0637 \u0627\u0644\u062c\u0627\u0646\u0628\u064a \u0644\u0640 Sokuji',
        content:
          '\u0627\u0646\u0642\u0631 \u0639\u0644\u0649 \u0623\u064a\u0642\u0648\u0646\u0629 \u0625\u0636\u0627\u0641\u0629 Sokuji \u0641\u064a \u0627\u0644\u0632\u0627\u0648\u064a\u0629 \u0627\u0644\u0639\u0644\u0648\u064a\u0629 \u0627\u0644\u064a\u0645\u0646\u0649 \u0645\u0646 \u0645\u062a\u0635\u0641\u062d\u0643\u060c \u062b\u0645 \u0627\u0646\u0642\u0631 \u0644\u0641\u062a\u062d \u0644\u0648\u062d\u0629 Sokuji \u0627\u0644\u062c\u0627\u0646\u0628\u064a\u0629.',
        screenshot: '4.png',
        tip: '\u0633\u064a\u062a\u0645 \u0641\u062a\u062d \u0627\u0644\u0634\u0631\u064a\u0637 \u0627\u0644\u062c\u0627\u0646\u0628\u064a \u0639\u0644\u0649 \u0627\u0644\u062c\u0627\u0646\u0628 \u0627\u0644\u0623\u064a\u0645\u0646 \u0645\u0646 \u0646\u0627\u0641\u0630\u0629 \u0627\u0644\u0645\u062a\u0635\u0641\u062d.',
      },
      {
        title: '\u0642\u0645 \u0628\u062a\u0643\u0648\u064a\u0646 \u0625\u0639\u062f\u0627\u062f\u0627\u062a Sokuji',
        content:
          '\u0641\u064a \u0627\u0644\u0634\u0631\u064a\u0637 \u0627\u0644\u062c\u0627\u0646\u0628\u064a \u0644\u0640 Sokuji\u060c \u0642\u0645 \u0628\u062a\u0643\u0648\u064a\u0646 \u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u062a\u0631\u062c\u0645\u0629 \u0628\u0645\u0627 \u0641\u064a \u0630\u0644\u0643 \u0644\u063a\u0627\u062a \u0627\u0644\u0645\u0635\u062f\u0631 \u0648\u0627\u0644\u0647\u062f\u0641 \u0648\u0645\u0632\u0648\u062f \u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064a \u0648\u0627\u0644\u062a\u0641\u0636\u064a\u0644\u0627\u062a \u0627\u0644\u0623\u062e\u0631\u0649.',
        screenshot: '5.png',
        tip: '\u062a\u0623\u0643\u062f \u0645\u0646 \u0627\u062e\u062a\u0628\u0627\u0631 \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0648\u0627\u062e\u062a\u064a\u0627\u0631 \u062c\u0647\u0627\u0632 \u0627\u0644\u0625\u062f\u062e\u0627\u0644 \u0627\u0644\u0645\u0646\u0627\u0633\u0628.',
      },
      {
        title: '\u062d\u062f\u062f \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0627\u0644\u0627\u0641\u062a\u0631\u0627\u0636\u064a \u0641\u064a Zoom',
        content:
          '\u0641\u064a \u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u0635\u0648\u062a \u0641\u064a Zoom\u060c \u0627\u0646\u0642\u0631 \u0639\u0644\u0649 \u0627\u0644\u0642\u0627\u0626\u0645\u0629 \u0627\u0644\u0645\u0646\u0633\u062f\u0644\u0629 \u0644\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0648\u062d\u062f\u062f <strong>Sokuji Virtual Microphone</strong> \u0643\u062c\u0647\u0627\u0632 \u0625\u062f\u062e\u0627\u0644.',
        screenshot: '6.png',
        tip: '\u064a\u0645\u0643\u0646\u0643 \u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u0635\u0648\u062a \u0628\u0627\u0644\u0646\u0642\u0631 \u0639\u0644\u0649 \u0627\u0644\u0633\u0647\u0645 \u0628\u062c\u0627\u0646\u0628 \u0623\u064a\u0642\u0648\u0646\u0629 \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0641\u064a Zoom.',
      },
    ],
    tips: {
      title: '\u0646\u0635\u0627\u0626\u062d \u0644\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0623\u0641\u0636\u0644 \u0627\u0644\u0646\u062a\u0627\u0626\u062c',
      items: [
        '\u062a\u062d\u062f\u062b \u0628\u0648\u0636\u0648\u062d \u0648\u062a\u062c\u0646\u0628 \u0627\u0644\u0636\u0648\u0636\u0627\u0621 \u0627\u0644\u062e\u0644\u0641\u064a\u0629',
        '\u062a\u0648\u0642\u0641 \u0644\u0641\u062a\u0631\u0629 \u0648\u062c\u064a\u0632\u0629 \u0628\u064a\u0646 \u0627\u0644\u062c\u0645\u0644 \u0644\u062a\u062d\u0633\u064a\u0646 \u062f\u0642\u0629 \u0627\u0644\u062a\u0631\u062c\u0645\u0629',
        '\u0627\u062e\u062a\u0628\u0631 \u0627\u0644\u0635\u0648\u062a \u0642\u0628\u0644 \u0627\u0644\u0627\u062c\u062a\u0645\u0627\u0639\u0627\u062a \u0627\u0644\u0645\u0647\u0645\u0629',
      ],
    },
    faq: {
      title: '\u0627\u0644\u0623\u0633\u0626\u0644\u0629 \u0627\u0644\u0634\u0627\u0626\u0639\u0629',
      items: [
        {
          question: '\u0644\u0645\u0627\u0630\u0627 \u0644\u0627 \u0623\u0633\u062a\u0637\u064a\u0639 \u0631\u0624\u064a\u0629 Sokuji Virtual Microphone\u061f',
          answer:
            '\u062a\u0623\u0643\u062f \u0645\u0646 \u0623\u0646 \u0625\u0636\u0627\u0641\u0629 Sokuji \u0645\u062b\u0628\u062a\u0629 \u0648\u0646\u0634\u0637\u0629. \u062d\u0627\u0648\u0644 \u062a\u062d\u062f\u064a\u062b \u0635\u0641\u062d\u0629 Zoom. \u0625\u0630\u0627 \u0627\u0633\u062a\u0645\u0631\u062a \u0627\u0644\u0645\u0634\u0643\u0644\u0629\u060c \u062a\u062d\u0642\u0642 \u0645\u0646 \u0623\u0646\u0643 \u0645\u0646\u062d\u062a \u0623\u0630\u0648\u0646\u0627\u062a \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0644\u0644\u0625\u0636\u0627\u0641\u0629.',
        },
        {
          question: '\u0647\u0644 \u064a\u0645\u0643\u0646 \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u064a\u0646 \u0627\u0644\u0622\u062e\u0631\u064a\u0646 \u0633\u0645\u0627\u0639 \u0643\u0644\u062a\u0627 \u0627\u0644\u0644\u063a\u062a\u064a\u0646\u061f',
          answer:
            'بشكل افتراضي، يسمع المشاركون الصوت المترجم فقط. إذا فعّلت "التمرير المباشر" في قسم "الكلام" في Sokuji، فسيسمع المشاركون صوتك الأصلي تحت الترجمة في آن واحد. هذه الميزة مفيدة عندما تريد أن يسمع المستمعون كلتا اللغتين للسياق أو التحقق.',
        },
        {
          question: '\u0647\u0644 \u064a\u0639\u0645\u0644 Sokuji \u0645\u0639 \u062a\u0633\u062c\u064a\u0644\u0627\u062a Zoom\u061f',
          answer:
            '\u0646\u0639\u0645\u060c \u0633\u064a\u062a\u0645 \u062a\u0636\u0645\u064a\u0646 \u0627\u0644\u0635\u0648\u062a \u0627\u0644\u0645\u062a\u0631\u062c\u0645 \u0641\u064a \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u0627\u062c\u062a\u0645\u0627\u0639 \u0625\u0630\u0627 \u0643\u0646\u062a \u062a\u0633\u062a\u062e\u062f\u0645 Sokuji Virtual Microphone.',
        },
        {
          question: 'ما هو "التمرير المباشر"؟',
          answer:
            '"التمرير المباشر" يمزج صوتك بمستوى أخفض تحت الترجمة في الميكروفون الافتراضي، فيسمع المستمعون كلتا اللغتين في آن واحد. وهو صف فرعي تحت "الترجمة التي يسمعها الطرف الآخر" في قسم "الكلام" (وضع "سريع" في الإعدادات، أو الإعدادات ← متقدم ← الصوت)، وله شريط لمستوى الصوت: 20% افتراضيًا، و60% كحد أقصى. ويظهر باهتًا ما دامت "الترجمة التي يسمعها الطرف الآخر" متوقفة. هذا مفيد بشكل خاص للجماهير ثنائية اللغة أو عندما تريد الحفاظ على النبرة العاطفية والفروق الدقيقة في خطابك الأصلي.',
        },
      ],
    },
    troubleshooting: {
      title: '\u0627\u0633\u062a\u0643\u0634\u0627\u0641 \u0627\u0644\u0623\u062e\u0637\u0627\u0621 \u0648\u0625\u0635\u0644\u0627\u062d\u0647\u0627',
      content:
        '\u0625\u0630\u0627 \u0648\u0627\u062c\u0647\u062a \u0623\u064a \u0645\u0634\u0627\u0643\u0644\u060c \u064a\u0631\u062c\u0649 \u0632\u064a\u0627\u0631\u0629 <a href="https://github.com/kizuna-ai-lab/sokuji">\u0645\u0633\u062a\u0648\u062f\u0639 GitHub</a> \u0627\u0644\u062e\u0627\u0635 \u0628\u0646\u0627 \u0623\u0648 \u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627 \u0639\u0628\u0631 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> \u0644\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u062f\u0639\u0645.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Zoom',
    backLink: 'Voltar para sites suportados',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Vis\u00e3o geral',
      content:
        'Este guia ir\u00e1 orient\u00e1-lo na configura\u00e7\u00e3o e uso do Sokuji com o Zoom para tradu\u00e7\u00e3o de idiomas em tempo real durante suas reuni\u00f5es.',
    },
    steps: [
      {
        title: 'Abra o link da reuni\u00e3o do Zoom',
        content:
          'Abra o link da sua reuni\u00e3o do Zoom no navegador Chrome ou Edge. O navegador tentar\u00e1 abrir o aplicativo de desktop do Zoom.',
        screenshot: '1.png',
        tip: 'Certifique-se de que a extens\u00e3o Sokuji esteja instalada e ativada no seu navegador.',
      },
      {
        title: 'Recuse o aplicativo nativo',
        content:
          'Quando solicitado a abrir o aplicativo de desktop do Zoom, clique em <strong>Cancelar</strong> ou feche a caixa de di\u00e1logo para permanecer no navegador.',
        screenshot: '2.png',
        tip: 'Isso \u00e9 importante \u2013 voc\u00ea deve usar a vers\u00e3o do navegador do Zoom para que o Sokuji funcione.',
      },
      {
        title: 'Participar pelo navegador',
        content:
          'Ap\u00f3s recusar o aplicativo de desktop, procure o link <strong>Participar pelo seu navegador</strong> que aparece na p\u00e1gina e clique nele.',
        screenshot: '3.png',
        tip: 'Este link pode aparecer na parte inferior da p\u00e1gina ou em uma mensagem de notifica\u00e7\u00e3o.',
      },
      {
        title: 'Abra a barra lateral do Sokuji',
        content:
          'Clique no \u00edcone da extens\u00e3o Sokuji no canto superior direito do seu navegador e clique para abrir o painel lateral do Sokuji.',
        screenshot: '4.png',
        tip: 'A barra lateral ser\u00e1 aberta no lado direito da janela do navegador.',
      },
      {
        title: 'Configure as defini\u00e7\u00f5es do Sokuji',
        content:
          'Na barra lateral do Sokuji, configure suas defini\u00e7\u00f5es de tradu\u00e7\u00e3o, incluindo os idiomas de origem e destino, o provedor de IA e outras prefer\u00eancias.',
        screenshot: '5.png',
        tip: 'Certifique-se de testar seu microfone e selecionar o dispositivo de entrada adequado.',
      },
      {
        title: 'Selecione o microfone virtual no Zoom',
        content:
          'Nas configura\u00e7\u00f5es de \u00e1udio do Zoom, clique no menu suspenso do microfone e selecione <strong>Sokuji Virtual Microphone</strong> como seu dispositivo de entrada.',
        screenshot: '6.png',
        tip: 'Voc\u00ea pode acessar as configura\u00e7\u00f5es de \u00e1udio clicando na seta ao lado do \u00edcone do microfone no Zoom.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Fale com clareza e evite ru\u00eddo de fundo',
        'Fa\u00e7a uma breve pausa entre as frases para melhorar a precis\u00e3o da tradu\u00e7\u00e3o',
        'Teste seu \u00e1udio antes de reuni\u00f5es importantes',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'Por que n\u00e3o consigo ver o Sokuji Virtual Microphone?',
          answer:
            'Certifique-se de que a extens\u00e3o Sokuji esteja instalada e ativa. Tente atualizar a p\u00e1gina do Zoom. Se o problema persistir, verifique se voc\u00ea concedeu permiss\u00f5es de microfone \u00e0 extens\u00e3o.',
        },
        {
          question: 'Os outros participantes podem ouvir ambos os idiomas?',
          answer:
            'Por padrão, os participantes ouvem apenas o áudio traduzido. Se você ativar "Passagem direta" no bloco "Voz" do Sokuji, os participantes ouvirão sua voz original por baixo da tradução ao mesmo tempo. Este recurso é útil quando você deseja que os ouvintes ouçam ambos os idiomas para contexto ou verificação.',
        },
        {
          question: 'O Sokuji funciona com as grava\u00e7\u00f5es do Zoom?',
          answer:
            'Sim, o \u00e1udio traduzido ser\u00e1 inclu\u00eddo na grava\u00e7\u00e3o da reuni\u00e3o se voc\u00ea estiver usando o Sokuji Virtual Microphone.',
        },
        {
          question: 'O que é a "Passagem direta"?',
          answer:
            'A "Passagem direta" mistura sua própria voz, em volume mais baixo, por baixo da tradução no microfone virtual, para que os ouvintes ouçam os dois idiomas ao mesmo tempo. Ela é uma subopção de "Tradução que o outro lado ouve" no bloco "Voz" (configurações "Rápido", ou Configurações → Avançado → Áudio), com um controle de volume: 20% por padrão, no máximo 60%. Fica esmaecida enquanto "Tradução que o outro lado ouve" está desligada. Isso é particularmente útil para audiências bilíngues ou quando você deseja manter o tom emocional e as nuances do seu discurso original.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solu\u00e7\u00e3o de problemas',
      content:
        'Se voc\u00ea encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">reposit\u00f3rio GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: '\u0418\u0441\u043f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u043d\u0438\u0435 Sokuji \u0441 Zoom',
    backLink: '\u041d\u0430\u0437\u0430\u0434 \u043a \u043f\u043e\u0434\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u043c\u044b\u043c \u0441\u0430\u0439\u0442\u0430\u043c',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u041e\u0431\u0437\u043e\u0440',
      content:
        '\u042d\u0442\u043e \u0440\u0443\u043a\u043e\u0432\u043e\u0434\u0441\u0442\u0432\u043e \u043f\u043e\u043c\u043e\u0436\u0435\u0442 \u0432\u0430\u043c \u043d\u0430\u0441\u0442\u0440\u043e\u0438\u0442\u044c \u0438 \u0438\u0441\u043f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u0442\u044c Sokuji \u0441 Zoom \u0434\u043b\u044f \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430 \u0432 \u0440\u0435\u0430\u043b\u044c\u043d\u043e\u043c \u0432\u0440\u0435\u043c\u0435\u043d\u0438 \u0432\u043e \u0432\u0440\u0435\u043c\u044f \u0432\u0430\u0448\u0438\u0445 \u0432\u0441\u0442\u0440\u0435\u0447.',
    },
    steps: [
      {
        title: '\u041e\u0442\u043a\u0440\u043e\u0439\u0442\u0435 \u0441\u0441\u044b\u043b\u043a\u0443 \u043d\u0430 \u0432\u0441\u0442\u0440\u0435\u0447\u0443 Zoom',
        content:
          '\u041e\u0442\u043a\u0440\u043e\u0439\u0442\u0435 \u0441\u0441\u044b\u043b\u043a\u0443 \u043d\u0430 \u0432\u0441\u0442\u0440\u0435\u0447\u0443 Zoom \u0432 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0435 Chrome \u0438\u043b\u0438 Edge. \u0411\u0440\u0430\u0443\u0437\u0435\u0440 \u043f\u043e\u043f\u044b\u0442\u0430\u0435\u0442\u0441\u044f \u043e\u0442\u043a\u0440\u044b\u0442\u044c \u043d\u0430\u0441\u0442\u043e\u043b\u044c\u043d\u043e\u0435 \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u0435 Zoom.',
        screenshot: '1.png',
        tip: '\u0423\u0431\u0435\u0434\u0438\u0442\u0435\u0441\u044c, \u0447\u0442\u043e \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u0435 Sokuji \u0443\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0438 \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u043e \u0432 \u0432\u0430\u0448\u0435\u043c \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0435.',
      },
      {
        title: '\u041e\u0442\u043a\u043b\u043e\u043d\u0438\u0442\u0435 \u043d\u0430\u0442\u0438\u0432\u043d\u043e\u0435 \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u0435',
        content:
          '\u041a\u043e\u0433\u0434\u0430 \u043f\u043e\u044f\u0432\u0438\u0442\u0441\u044f \u0437\u0430\u043f\u0440\u043e\u0441 \u043d\u0430 \u043e\u0442\u043a\u0440\u044b\u0442\u0438\u0435 \u043d\u0430\u0441\u0442\u043e\u043b\u044c\u043d\u043e\u0433\u043e \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u044f Zoom, \u043d\u0430\u0436\u043c\u0438\u0442\u0435 <strong>\u041e\u0442\u043c\u0435\u043d\u0430</strong> \u0438\u043b\u0438 \u0437\u0430\u043a\u0440\u043e\u0439\u0442\u0435 \u0434\u0438\u0430\u043b\u043e\u0433\u043e\u0432\u043e\u0435 \u043e\u043a\u043d\u043e, \u0447\u0442\u043e\u0431\u044b \u043e\u0441\u0442\u0430\u0442\u044c\u0441\u044f \u0432 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0435.',
        screenshot: '2.png',
        tip: '\u042d\u0442\u043e \u0432\u0430\u0436\u043d\u043e \u2014 \u0434\u043b\u044f \u0440\u0430\u0431\u043e\u0442\u044b Sokuji \u043d\u0435\u043e\u0431\u0445\u043e\u0434\u0438\u043c\u043e \u0438\u0441\u043f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u0442\u044c \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u043d\u0443\u044e \u0432\u0435\u0440\u0441\u0438\u044e Zoom.',
      },
      {
        title: '\u041f\u0440\u0438\u0441\u043e\u0435\u0434\u0438\u043d\u0438\u0442\u0435\u0441\u044c \u0447\u0435\u0440\u0435\u0437 \u0431\u0440\u0430\u0443\u0437\u0435\u0440',
        content:
          '\u041f\u043e\u0441\u043b\u0435 \u043e\u0442\u043a\u043b\u043e\u043d\u0435\u043d\u0438\u044f \u043d\u0430\u0441\u0442\u043e\u043b\u044c\u043d\u043e\u0433\u043e \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u044f \u043d\u0430\u0439\u0434\u0438\u0442\u0435 \u0441\u0441\u044b\u043b\u043a\u0443 <strong>\u041f\u0440\u0438\u0441\u043e\u0435\u0434\u0438\u043d\u0438\u0442\u044c\u0441\u044f \u0438\u0437 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430</strong>, \u043a\u043e\u0442\u043e\u0440\u0430\u044f \u043f\u043e\u044f\u0432\u0438\u0442\u0441\u044f \u043d\u0430 \u0441\u0442\u0440\u0430\u043d\u0438\u0446\u0435, \u0438 \u043d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u043d\u0435\u0435.',
        screenshot: '3.png',
        tip: '\u042d\u0442\u0430 \u0441\u0441\u044b\u043b\u043a\u0430 \u043c\u043e\u0436\u0435\u0442 \u043f\u043e\u044f\u0432\u0438\u0442\u044c\u0441\u044f \u0432\u043d\u0438\u0437\u0443 \u0441\u0442\u0440\u0430\u043d\u0438\u0446\u044b \u0438\u043b\u0438 \u0432 \u0443\u0432\u0435\u0434\u043e\u043c\u043b\u0435\u043d\u0438\u0438.',
      },
      {
        title: '\u041e\u0442\u043a\u0440\u043e\u0439\u0442\u0435 \u0431\u043e\u043a\u043e\u0432\u0443\u044e \u043f\u0430\u043d\u0435\u043b\u044c Sokuji',
        content:
          '\u041d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u0437\u043d\u0430\u0447\u043e\u043a \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u044f Sokuji \u0432 \u043f\u0440\u0430\u0432\u043e\u043c \u0432\u0435\u0440\u0445\u043d\u0435\u043c \u0443\u0433\u043b\u0443 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430, \u0437\u0430\u0442\u0435\u043c \u043d\u0430\u0436\u043c\u0438\u0442\u0435, \u0447\u0442\u043e\u0431\u044b \u043e\u0442\u043a\u0440\u044b\u0442\u044c \u0431\u043e\u043a\u043e\u0432\u0443\u044e \u043f\u0430\u043d\u0435\u043b\u044c Sokuji.',
        screenshot: '4.png',
        tip: '\u0411\u043e\u043a\u043e\u0432\u0430\u044f \u043f\u0430\u043d\u0435\u043b\u044c \u043e\u0442\u043a\u0440\u043e\u0435\u0442\u0441\u044f \u0432 \u043f\u0440\u0430\u0432\u043e\u0439 \u0447\u0430\u0441\u0442\u0438 \u043e\u043a\u043d\u0430 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430.',
      },
      {
        title: '\u041d\u0430\u0441\u0442\u0440\u043e\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u044b Sokuji',
        content:
          '\u0412 \u0431\u043e\u043a\u043e\u0432\u043e\u0439 \u043f\u0430\u043d\u0435\u043b\u0438 Sokuji \u043d\u0430\u0441\u0442\u0440\u043e\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u044b \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430, \u0432\u043a\u043b\u044e\u0447\u0430\u044f \u0438\u0441\u0445\u043e\u0434\u043d\u044b\u0439 \u0438 \u0446\u0435\u043b\u0435\u0432\u043e\u0439 \u044f\u0437\u044b\u043a\u0438, \u043f\u043e\u0441\u0442\u0430\u0432\u0449\u0438\u043a\u0430 \u0418\u0418 \u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u043f\u0440\u0435\u0434\u043f\u043e\u0447\u0442\u0435\u043d\u0438\u044f.',
        screenshot: '5.png',
        tip: '\u041e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u043e \u043f\u0440\u043e\u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0439\u0442\u0435 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d \u0438 \u0432\u044b\u0431\u0435\u0440\u0438\u0442\u0435 \u043f\u043e\u0434\u0445\u043e\u0434\u044f\u0449\u0435\u0435 \u0443\u0441\u0442\u0440\u043e\u0439\u0441\u0442\u0432\u043e \u0432\u0432\u043e\u0434\u0430.',
      },
      {
        title: '\u0412\u044b\u0431\u0435\u0440\u0438\u0442\u0435 \u0432\u0438\u0440\u0442\u0443\u0430\u043b\u044c\u043d\u044b\u0439 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d \u0432 Zoom',
        content:
          '\u0412 \u043d\u0430\u0441\u0442\u0440\u043e\u0439\u043a\u0430\u0445 \u0430\u0443\u0434\u0438\u043e Zoom \u043d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u0432\u044b\u043f\u0430\u0434\u0430\u044e\u0449\u0435\u0435 \u043c\u0435\u043d\u044e \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d\u0430 \u0438 \u0432\u044b\u0431\u0435\u0440\u0438\u0442\u0435 <strong>Sokuji Virtual Microphone</strong> \u0432 \u043a\u0430\u0447\u0435\u0441\u0442\u0432\u0435 \u0443\u0441\u0442\u0440\u043e\u0439\u0441\u0442\u0432\u0430 \u0432\u0432\u043e\u0434\u0430.',
        screenshot: '6.png',
        tip: '\u0412\u044b \u043c\u043e\u0436\u0435\u0442\u0435 \u043f\u043e\u043b\u0443\u0447\u0438\u0442\u044c \u0434\u043e\u0441\u0442\u0443\u043f \u043a \u043d\u0430\u0441\u0442\u0440\u043e\u0439\u043a\u0430\u043c \u0430\u0443\u0434\u0438\u043e, \u043d\u0430\u0436\u0430\u0432 \u043d\u0430 \u0441\u0442\u0440\u0435\u043b\u043a\u0443 \u0440\u044f\u0434\u043e\u043c \u0441\u043e \u0437\u043d\u0430\u0447\u043a\u043e\u043c \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d\u0430 \u0432 Zoom.',
      },
    ],
    tips: {
      title: '\u0421\u043e\u0432\u0435\u0442\u044b \u0434\u043b\u044f \u043b\u0443\u0447\u0448\u0438\u0445 \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u043e\u0432',
      items: [
        '\u0413\u043e\u0432\u043e\u0440\u0438\u0442\u0435 \u0447\u0435\u0442\u043a\u043e \u0438 \u0438\u0437\u0431\u0435\u0433\u0430\u0439\u0442\u0435 \u0444\u043e\u043d\u043e\u0432\u043e\u0433\u043e \u0448\u0443\u043c\u0430',
        '\u0414\u0435\u043b\u0430\u0439\u0442\u0435 \u043a\u043e\u0440\u043e\u0442\u043a\u0438\u0435 \u043f\u0430\u0443\u0437\u044b \u043c\u0435\u0436\u0434\u0443 \u043f\u0440\u0435\u0434\u043b\u043e\u0436\u0435\u043d\u0438\u044f\u043c\u0438 \u0434\u043b\u044f \u043b\u0443\u0447\u0448\u0435\u0439 \u0442\u043e\u0447\u043d\u043e\u0441\u0442\u0438 \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430',
        '\u041f\u0440\u043e\u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0439\u0442\u0435 \u0430\u0443\u0434\u0438\u043e \u043f\u0435\u0440\u0435\u0434 \u0432\u0430\u0436\u043d\u044b\u043c\u0438 \u0432\u0441\u0442\u0440\u0435\u0447\u0430\u043c\u0438',
      ],
    },
    faq: {
      title: '\u0427\u0430\u0441\u0442\u043e \u0437\u0430\u0434\u0430\u0432\u0430\u0435\u043c\u044b\u0435 \u0432\u043e\u043f\u0440\u043e\u0441\u044b',
      items: [
        {
          question: '\u041f\u043e\u0447\u0435\u043c\u0443 \u044f \u043d\u0435 \u0432\u0438\u0436\u0443 Sokuji Virtual Microphone?',
          answer:
            '\u0423\u0431\u0435\u0434\u0438\u0442\u0435\u0441\u044c, \u0447\u0442\u043e \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u0435 Sokuji \u0443\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0438 \u0430\u043a\u0442\u0438\u0432\u043d\u043e. \u041f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u043e\u0431\u043d\u043e\u0432\u0438\u0442\u044c \u0441\u0442\u0440\u0430\u043d\u0438\u0446\u0443 Zoom. \u0415\u0441\u043b\u0438 \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u0430 \u043d\u0435 \u0438\u0441\u0447\u0435\u0437\u043d\u0435\u0442, \u043f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435, \u0447\u0442\u043e \u0432\u044b \u043f\u0440\u0435\u0434\u043e\u0441\u0442\u0430\u0432\u0438\u043b\u0438 \u0440\u0430\u0437\u0440\u0435\u0448\u0435\u043d\u0438\u044f \u043d\u0430 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u044e.',
        },
        {
          question: '\u041c\u043e\u0433\u0443\u0442 \u043b\u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u0443\u0447\u0430\u0441\u0442\u043d\u0438\u043a\u0438 \u0441\u043b\u044b\u0448\u0430\u0442\u044c \u043e\u0431\u0430 \u044f\u0437\u044b\u043a\u0430?',
          answer:
            'По умолчанию участники слышат только переведённое аудио. Если включить «Passthrough» в блоке «Речь» в Sokuji, участники одновременно услышат и ваш оригинальный голос под переводом. Эта функция полезна, когда вы хотите, чтобы слушатели слышали оба языка для контекста или проверки.',
        },
        {
          question: '\u0420\u0430\u0431\u043e\u0442\u0430\u0435\u0442 \u043b\u0438 Sokuji \u0441 \u0437\u0430\u043f\u0438\u0441\u044f\u043c\u0438 Zoom?',
          answer:
            '\u0414\u0430, \u043f\u0435\u0440\u0435\u0432\u0435\u0434\u0435\u043d\u043d\u043e\u0435 \u0430\u0443\u0434\u0438\u043e \u0431\u0443\u0434\u0435\u0442 \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u043e \u0432 \u0437\u0430\u043f\u0438\u0441\u044c \u0432\u0441\u0442\u0440\u0435\u0447\u0438, \u0435\u0441\u043b\u0438 \u0432\u044b \u0438\u0441\u043f\u043e\u043b\u044c\u0437\u0443\u0435\u0442\u0435 Sokuji Virtual Microphone.',
        },
        {
          question: 'Что такое Passthrough?',
          answer:
            'Passthrough подмешивает ваш собственный голос тише под перевод в виртуальный микрофон, чтобы слушатели слышали оба языка одновременно. Это подпункт строки «Перевод, который слышит собеседник» в блоке «Речь» (режим «Быстрый» в настройках или Настройки → Расширенный → Аудио), с ползунком громкости: 20% по умолчанию, не более 60%. Пока «Перевод, который слышит собеседник» выключен, он неактивен (серый). Это особенно полезно для двуязычной аудитории или когда вы хотите сохранить эмоциональный тон и нюансы оригинальной речи.',
        },
      ],
    },
    troubleshooting: {
      title: '\u0423\u0441\u0442\u0440\u0430\u043d\u0435\u043d\u0438\u0435 \u043d\u0435\u043f\u043e\u043b\u0430\u0434\u043e\u043a',
      content:
        '\u0415\u0441\u043b\u0438 \u0443 \u0432\u0430\u0441 \u0432\u043e\u0437\u043d\u0438\u043a\u043b\u0438 \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u044b, \u043f\u043e\u0441\u0435\u0442\u0438\u0442\u0435 \u043d\u0430\u0448 <a href="https://github.com/kizuna-ai-lab/sokuji">\u0440\u0435\u043f\u043e\u0437\u0438\u0442\u043e\u0440\u0438\u0439 GitHub</a> \u0438\u043b\u0438 \u0441\u0432\u044f\u0436\u0438\u0442\u0435\u0441\u044c \u0441 \u043d\u0430\u043c\u0438 \u043f\u043e \u0430\u0434\u0440\u0435\u0441\u0443 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> \u0434\u043b\u044f \u043f\u043e\u043b\u0443\u0447\u0435\u043d\u0438\u044f \u043f\u043e\u0434\u0434\u0435\u0440\u0436\u043a\u0438.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Zoom verwenden',
    backLink: 'Zur\u00fcck zu unterst\u00fctzten Websites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u00dcbersicht',
      content:
        'Diese Anleitung f\u00fchrt Sie durch die Einrichtung und Verwendung von Sokuji mit Zoom f\u00fcr Echtzeit-Sprach\u00fcbersetzung w\u00e4hrend Ihrer Besprechungen.',
    },
    steps: [
      {
        title: '\u00d6ffnen Sie den Zoom-Meeting-Link',
        content:
          '\u00d6ffnen Sie Ihren Zoom-Meeting-Link im Chrome- oder Edge-Browser. Der Browser wird versuchen, die Zoom-Desktop-Anwendung zu \u00f6ffnen.',
        screenshot: '1.png',
        tip: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Ihrem Browser installiert und aktiviert ist.',
      },
      {
        title: 'Native App ablehnen',
        content:
          'Wenn Sie aufgefordert werden, die Zoom-Desktop-Anwendung zu \u00f6ffnen, klicken Sie auf <strong>Abbrechen</strong> oder schlie\u00dfen Sie das Dialogfeld, um im Browser zu bleiben.',
        screenshot: '2.png',
        tip: 'Dies ist wichtig \u2013 Sie m\u00fcssen die Browser-Version von Zoom verwenden, damit Sokuji funktioniert.',
      },
      {
        title: '\u00dcber den Browser beitreten',
        content:
          'Nachdem Sie die Desktop-App abgelehnt haben, suchen Sie den Link <strong>\u00dcber Ihren Browser beitreten</strong>, der auf der Seite erscheint, und klicken Sie darauf.',
        screenshot: '3.png',
        tip: 'Dieser Link kann unten auf der Seite oder in einer Benachrichtigung erscheinen.',
      },
      {
        title: 'Sokuji-Seitenleiste \u00f6ffnen',
        content:
          'Klicken Sie auf das Sokuji-Erweiterungssymbol in der oberen rechten Ecke Ihres Browsers und klicken Sie dann, um das Sokuji-Seitenpanel zu \u00f6ffnen.',
        screenshot: '4.png',
        tip: 'Die Seitenleiste \u00f6ffnet sich auf der rechten Seite Ihres Browserfensters.',
      },
      {
        title: 'Sokuji-Einstellungen konfigurieren',
        content:
          'Konfigurieren Sie in der Sokuji-Seitenleiste Ihre \u00dcbersetzungseinstellungen, einschlie\u00dflich Quell- und Zielsprache, KI-Anbieter und andere Einstellungen.',
        screenshot: '5.png',
        tip: 'Stellen Sie sicher, dass Sie Ihr Mikrofon testen und das richtige Eingabeger\u00e4t ausw\u00e4hlen.',
      },
      {
        title: 'Virtuelles Mikrofon in Zoom ausw\u00e4hlen',
        content:
          'Klicken Sie in den Audioeinstellungen von Zoom auf das Mikrofon-Dropdown und w\u00e4hlen Sie <strong>Sokuji Virtual Microphone</strong> als Eingabeger\u00e4t.',
        screenshot: '6.png',
        tip: 'Sie k\u00f6nnen auf die Audioeinstellungen zugreifen, indem Sie auf den Pfeil neben dem Mikrofonsymbol in Zoom klicken.',
      },
    ],
    tips: {
      title: 'Tipps f\u00fcr beste Ergebnisse',
      items: [
        'Sprechen Sie deutlich und vermeiden Sie Hintergrundger\u00e4usche',
        'Machen Sie kurze Pausen zwischen S\u00e4tzen f\u00fcr bessere \u00dcbersetzungsgenauigkeit',
        'Testen Sie Ihr Audio vor wichtigen Besprechungen',
      ],
    },
    faq: {
      title: 'H\u00e4ufig gestellte Fragen',
      items: [
        {
          question: 'Warum kann ich Sokuji Virtual Microphone nicht sehen?',
          answer:
            'Stellen Sie sicher, dass die Sokuji-Erweiterung installiert und aktiv ist. Versuchen Sie, die Zoom-Seite zu aktualisieren. Wenn das Problem weiterhin besteht, \u00fcberpr\u00fcfen Sie, ob Sie der Erweiterung Mikrofonberechtigungen erteilt haben.',
        },
        {
          question: 'K\u00f6nnen andere Teilnehmer beide Sprachen h\u00f6ren?',
          answer:
            'Standardmäßig hören Teilnehmer nur das übersetzte Audio. Wenn Sie in Sokujis Block „Sprache“ die „Durchleitung“ einschalten, hören die Teilnehmer Ihre Originalstimme gleichzeitig unter der Übersetzung. Diese Funktion ist nützlich, wenn Sie möchten, dass Zuhörer beide Sprachen für Kontext oder Überprüfung hören.',
        },
        {
          question: 'Funktioniert Sokuji mit Zoom-Aufnahmen?',
          answer:
            'Ja, das \u00fcbersetzte Audio wird in die Besprechungsaufnahme aufgenommen, wenn Sie Sokuji Virtual Microphone verwenden.',
        },
        {
          question: 'Was ist die Durchleitung?',
          answer:
            'Die Durchleitung mischt Ihre eigene Stimme leiser unter die Übersetzung ins virtuelle Mikrofon, sodass Zuhörer beide Sprachen gleichzeitig hören. Sie ist eine Unterzeile von „Übersetzung, die das Gegenüber hört“ im Block „Sprache“ (Einstellungen „Schnell“ oder Einstellungen → Erweitert → Audio), mit einem Lautstärkeregler: standardmäßig 20 %, höchstens 60 %. Solange „Übersetzung, die das Gegenüber hört“ ausgeschaltet ist, ist sie ausgegraut. Dies ist besonders nützlich für zweisprachige Zielgruppen oder wenn Sie den emotionalen Ton und die Nuancen Ihrer Originalrede beibehalten möchten.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content:
        'Wenn Sie auf Probleme sto\u00dfen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> f\u00fcr Unterst\u00fctzung.',
    },
  },
  uk: {
    pageTitle: '\u0412\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u0430\u043d\u043d\u044f Sokuji \u0437 Zoom',
    backLink: '\u041d\u0430\u0437\u0430\u0434 \u0434\u043e \u043f\u0456\u0434\u0442\u0440\u0438\u043c\u0443\u0432\u0430\u043d\u0438\u0445 \u0441\u0430\u0439\u0442\u0456\u0432',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u041e\u0433\u043b\u044f\u0434',
      content:
        '\u0426\u0435\u0439 \u043f\u043e\u0441\u0456\u0431\u043d\u0438\u043a \u0434\u043e\u043f\u043e\u043c\u043e\u0436\u0435 \u0432\u0430\u043c \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u0442\u0438 \u0442\u0430 \u0432\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u043e\u0432\u0443\u0432\u0430\u0442\u0438 Sokuji \u0437 Zoom \u0434\u043b\u044f \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443 \u0432 \u0440\u0435\u0430\u043b\u044c\u043d\u043e\u043c\u0443 \u0447\u0430\u0441\u0456 \u043f\u0456\u0434 \u0447\u0430\u0441 \u0432\u0430\u0448\u0438\u0445 \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0435\u0439.',
    },
    steps: [
      {
        title: '\u0412\u0456\u0434\u043a\u0440\u0438\u0439\u0442\u0435 \u043f\u043e\u0441\u0438\u043b\u0430\u043d\u043d\u044f \u043d\u0430 \u0437\u0443\u0441\u0442\u0440\u0456\u0447 Zoom',
        content:
          '\u0412\u0456\u0434\u043a\u0440\u0438\u0439\u0442\u0435 \u043f\u043e\u0441\u0438\u043b\u0430\u043d\u043d\u044f \u043d\u0430 \u0437\u0443\u0441\u0442\u0440\u0456\u0447 Zoom \u0443 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0456 Chrome \u0430\u0431\u043e Edge. \u0411\u0440\u0430\u0443\u0437\u0435\u0440 \u0441\u043f\u0440\u043e\u0431\u0443\u0454 \u0432\u0456\u0434\u043a\u0440\u0438\u0442\u0438 \u043d\u0430\u0441\u0442\u0456\u043b\u044c\u043d\u0438\u0439 \u0434\u043e\u0434\u0430\u0442\u043e\u043a Zoom.',
        screenshot: '1.png',
        tip: '\u041f\u0435\u0440\u0435\u043a\u043e\u043d\u0430\u0439\u0442\u0435\u0441\u044f, \u0449\u043e \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji \u0432\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0442\u0430 \u0443\u0432\u0456\u043c\u043a\u043d\u0435\u043d\u043e \u0443 \u0432\u0430\u0448\u043e\u043c\u0443 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0456.',
      },
      {
        title: '\u0412\u0456\u0434\u0445\u0438\u043b\u0456\u0442\u044c \u043d\u0430\u0442\u0438\u0432\u043d\u0438\u0439 \u0434\u043e\u0434\u0430\u0442\u043e\u043a',
        content:
          '\u041a\u043e\u043b\u0438 \u0437\u2019\u044f\u0432\u0438\u0442\u044c\u0441\u044f \u0437\u0430\u043f\u0438\u0442 \u043d\u0430 \u0432\u0456\u0434\u043a\u0440\u0438\u0442\u0442\u044f \u043d\u0430\u0441\u0442\u0456\u043b\u044c\u043d\u043e\u0433\u043e \u0434\u043e\u0434\u0430\u0442\u043a\u0443 Zoom, \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c <strong>\u0421\u043a\u0430\u0441\u0443\u0432\u0430\u0442\u0438</strong> \u0430\u0431\u043e \u0437\u0430\u043a\u0440\u0438\u0439\u0442\u0435 \u0434\u0456\u0430\u043b\u043e\u0433\u043e\u0432\u0435 \u0432\u0456\u043a\u043d\u043e, \u0449\u043e\u0431 \u0437\u0430\u043b\u0438\u0448\u0438\u0442\u0438\u0441\u044f \u0432 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0456.',
        screenshot: '2.png',
        tip: '\u0426\u0435 \u0432\u0430\u0436\u043b\u0438\u0432\u043e \u2014 \u0434\u043b\u044f \u0440\u043e\u0431\u043e\u0442\u0438 Sokuji \u043d\u0435\u043e\u0431\u0445\u0456\u0434\u043d\u043e \u0432\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u043e\u0432\u0443\u0432\u0430\u0442\u0438 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u043d\u0443 \u0432\u0435\u0440\u0441\u0456\u044e Zoom.',
      },
      {
        title: '\u041f\u0440\u0438\u0454\u0434\u043d\u0430\u0439\u0442\u0435\u0441\u044f \u0447\u0435\u0440\u0435\u0437 \u0431\u0440\u0430\u0443\u0437\u0435\u0440',
        content:
          '\u041f\u0456\u0441\u043b\u044f \u0432\u0456\u0434\u0445\u0438\u043b\u0435\u043d\u043d\u044f \u043d\u0430\u0441\u0442\u0456\u043b\u044c\u043d\u043e\u0433\u043e \u0434\u043e\u0434\u0430\u0442\u043a\u0443 \u0437\u043d\u0430\u0439\u0434\u0456\u0442\u044c \u043f\u043e\u0441\u0438\u043b\u0430\u043d\u043d\u044f <strong>\u041f\u0440\u0438\u0454\u0434\u043d\u0430\u0442\u0438\u0441\u044f \u0437 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430</strong>, \u044f\u043a\u0435 \u0437\u2019\u044f\u0432\u0438\u0442\u044c\u0441\u044f \u043d\u0430 \u0441\u0442\u043e\u0440\u0456\u043d\u0446\u0456, \u0442\u0430 \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c \u043d\u0430 \u043d\u044c\u043e\u0433\u043e.',
        screenshot: '3.png',
        tip: '\u0426\u0435 \u043f\u043e\u0441\u0438\u043b\u0430\u043d\u043d\u044f \u043c\u043e\u0436\u0435 \u0437\u2019\u044f\u0432\u0438\u0442\u0438\u0441\u044f \u0432\u043d\u0438\u0437\u0443 \u0441\u0442\u043e\u0440\u0456\u043d\u043a\u0438 \u0430\u0431\u043e \u0432 \u043f\u043e\u0432\u0456\u0434\u043e\u043c\u043b\u0435\u043d\u043d\u0456.',
      },
      {
        title: '\u0412\u0456\u0434\u043a\u0440\u0438\u0439\u0442\u0435 \u0431\u0456\u0447\u043d\u0443 \u043f\u0430\u043d\u0435\u043b\u044c Sokuji',
        content:
          '\u041d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c \u043d\u0430 \u0456\u043a\u043e\u043d\u043a\u0443 \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji \u0443 \u043f\u0440\u0430\u0432\u043e\u043c\u0443 \u0432\u0435\u0440\u0445\u043d\u044c\u043e\u043c\u0443 \u043a\u0443\u0442\u043a\u0443 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430, \u043f\u043e\u0442\u0456\u043c \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c, \u0449\u043e\u0431 \u0432\u0456\u0434\u043a\u0440\u0438\u0442\u0438 \u0431\u0456\u0447\u043d\u0443 \u043f\u0430\u043d\u0435\u043b\u044c Sokuji.',
        screenshot: '4.png',
        tip: '\u0411\u0456\u0447\u043d\u0430 \u043f\u0430\u043d\u0435\u043b\u044c \u0432\u0456\u0434\u043a\u0440\u0438\u0454\u0442\u044c\u0441\u044f \u0432 \u043f\u0440\u0430\u0432\u0456\u0439 \u0447\u0430\u0441\u0442\u0438\u043d\u0456 \u0432\u0456\u043a\u043d\u0430 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430.',
      },
      {
        title: '\u041d\u0430\u043b\u0430\u0448\u0442\u0443\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u0438 Sokuji',
        content:
          '\u0423 \u0431\u0456\u0447\u043d\u0456\u0439 \u043f\u0430\u043d\u0435\u043b\u0456 Sokuji \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u0438 \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443, \u0432\u043a\u043b\u044e\u0447\u0430\u044e\u0447\u0438 \u043c\u043e\u0432\u0438 \u0434\u0436\u0435\u0440\u0435\u043b\u0430 \u0442\u0430 \u0446\u0456\u043b\u0456, \u043f\u043e\u0441\u0442\u0430\u0447\u0430\u043b\u044c\u043d\u0438\u043a\u0430 \u0428\u0406 \u0442\u0430 \u0456\u043d\u0448\u0456 \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u043d\u043d\u044f.',
        screenshot: '5.png',
        tip: '\u041e\u0431\u043e\u0432\u2019\u044f\u0437\u043a\u043e\u0432\u043e \u043f\u0440\u043e\u0442\u0435\u0441\u0442\u0443\u0439\u0442\u0435 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d \u0442\u0430 \u0432\u0438\u0431\u0435\u0440\u0456\u0442\u044c \u0432\u0456\u0434\u043f\u043e\u0432\u0456\u0434\u043d\u0438\u0439 \u043f\u0440\u0438\u0441\u0442\u0440\u0456\u0439 \u0432\u0432\u0435\u0434\u0435\u043d\u043d\u044f.',
      },
      {
        title: '\u0412\u0438\u0431\u0435\u0440\u0456\u0442\u044c \u0432\u0456\u0440\u0442\u0443\u0430\u043b\u044c\u043d\u0438\u0439 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d \u0443 Zoom',
        content:
          '\u0423 \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u043d\u043d\u044f\u0445 \u0430\u0443\u0434\u0456\u043e Zoom \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c \u043d\u0430 \u0432\u0438\u043f\u0430\u0434\u043d\u0435 \u043c\u0435\u043d\u044e \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d\u0430 \u0442\u0430 \u0432\u0438\u0431\u0435\u0440\u0456\u0442\u044c <strong>Sokuji Virtual Microphone</strong> \u044f\u043a \u043f\u0440\u0438\u0441\u0442\u0440\u0456\u0439 \u0432\u0432\u0435\u0434\u0435\u043d\u043d\u044f.',
        screenshot: '6.png',
        tip: '\u0412\u0438 \u043c\u043e\u0436\u0435\u0442\u0435 \u043e\u0442\u0440\u0438\u043c\u0430\u0442\u0438 \u0434\u043e\u0441\u0442\u0443\u043f \u0434\u043e \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u043d\u044c \u0430\u0443\u0434\u0456\u043e, \u043d\u0430\u0442\u0438\u0441\u043d\u0443\u0432\u0448\u0438 \u043d\u0430 \u0441\u0442\u0440\u0456\u043b\u043a\u0443 \u043f\u043e\u0440\u044f\u0434 \u0437 \u0456\u043a\u043e\u043d\u043a\u043e\u044e \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d\u0430 \u0432 Zoom.',
      },
    ],
    tips: {
      title: '\u041f\u043e\u0440\u0430\u0434\u0438 \u0434\u043b\u044f \u043d\u0430\u0439\u043a\u0440\u0430\u0449\u0438\u0445 \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u0456\u0432',
      items: [
        '\u0413\u043e\u0432\u043e\u0440\u0456\u0442\u044c \u0447\u0456\u0442\u043a\u043e \u0442\u0430 \u0443\u043d\u0438\u043a\u0430\u0439\u0442\u0435 \u0444\u043e\u043d\u043e\u0432\u043e\u0433\u043e \u0448\u0443\u043c\u0443',
        '\u0420\u043e\u0431\u0456\u0442\u044c \u043a\u043e\u0440\u043e\u0442\u043a\u0456 \u043f\u0430\u0443\u0437\u0438 \u043c\u0456\u0436 \u0440\u0435\u0447\u0435\u043d\u043d\u044f\u043c\u0438 \u0434\u043b\u044f \u043a\u0440\u0430\u0449\u043e\u0457 \u0442\u043e\u0447\u043d\u043e\u0441\u0442\u0456 \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443',
        '\u041f\u0440\u043e\u0442\u0435\u0441\u0442\u0443\u0439\u0442\u0435 \u0430\u0443\u0434\u0456\u043e \u043f\u0435\u0440\u0435\u0434 \u0432\u0430\u0436\u043b\u0438\u0432\u0438\u043c\u0438 \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0430\u043c\u0438',
      ],
    },
    faq: {
      title: '\u041f\u043e\u0448\u0438\u0440\u0435\u043d\u0456 \u0437\u0430\u043f\u0438\u0442\u0430\u043d\u043d\u044f',
      items: [
        {
          question: '\u0427\u043e\u043c\u0443 \u044f \u043d\u0435 \u0431\u0430\u0447\u0443 Sokuji Virtual Microphone?',
          answer:
            '\u041f\u0435\u0440\u0435\u043a\u043e\u043d\u0430\u0439\u0442\u0435\u0441\u044f, \u0449\u043e \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji \u0432\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0442\u0430 \u0430\u043a\u0442\u0438\u0432\u043d\u0435. \u0421\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u043e\u043d\u043e\u0432\u0438\u0442\u0438 \u0441\u0442\u043e\u0440\u0456\u043d\u043a\u0443 Zoom. \u042f\u043a\u0449\u043e \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u0430 \u043d\u0435 \u0437\u043d\u0438\u043a\u0430\u0454, \u043f\u0435\u0440\u0435\u0432\u0456\u0440\u0442\u0435, \u0447\u0438 \u043d\u0430\u0434\u0430\u043b\u0438 \u0432\u0438 \u0434\u043e\u0437\u0432\u0456\u043b \u043d\u0430 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044e.',
        },
        {
          question: '\u0427\u0438 \u043c\u043e\u0436\u0443\u0442\u044c \u0456\u043d\u0448\u0456 \u0443\u0447\u0430\u0441\u043d\u0438\u043a\u0438 \u0447\u0443\u0442\u0438 \u043e\u0431\u0438\u0434\u0432\u0456 \u043c\u043e\u0432\u0438?',
          answer:
            'За замовчуванням учасники чують лише перекладене аудіо. Якщо увімкнути «Passthrough» у блоці «Мовлення» в Sokuji, учасники одночасно чутимуть і ваш оригінальний голос під перекладом. Ця функція корисна, коли ви хочете, щоб слухачі чули обидві мови для контексту або перевірки.',
        },
        {
          question: '\u0427\u0438 \u043f\u0440\u0430\u0446\u044e\u0454 Sokuji \u0456\u0437 \u0437\u0430\u043f\u0438\u0441\u0430\u043c\u0438 Zoom?',
          answer:
            '\u0422\u0430\u043a, \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0435\u043d\u0435 \u0430\u0443\u0434\u0456\u043e \u0431\u0443\u0434\u0435 \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u043e \u0434\u043e \u0437\u0430\u043f\u0438\u0441\u0443 \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0456, \u044f\u043a\u0449\u043e \u0432\u0438 \u0432\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u043e\u0432\u0443\u0454\u0442\u0435 Sokuji Virtual Microphone.',
        },
        {
          question: 'Що таке Passthrough?',
          answer:
            'Passthrough підмішує ваш власний голос тихіше під переклад у віртуальний мікрофон, щоб слухачі чули обидві мови одночасно. Це підпункт рядка «Переклад, який чує співрозмовник» у блоці «Мовлення» (режим «Швидкий» у налаштуваннях або Налаштування → Розширений → Аудіо), з повзунком гучності: 20% за замовчуванням, не більше 60%. Поки «Переклад, який чує співрозмовник» вимкнено, він неактивний (сірий). Це особливо корисно для двомовної аудиторії або коли ви хочете зберегти емоційний тон та нюанси оригінальної мови.',
        },
      ],
    },
    troubleshooting: {
      title: '\u0423\u0441\u0443\u043d\u0435\u043d\u043d\u044f \u043d\u0435\u043f\u043e\u043b\u0430\u0434\u043e\u043a',
      content:
        '\u042f\u043a\u0449\u043e \u0443 \u0432\u0430\u0441 \u0432\u0438\u043d\u0438\u043a\u043b\u0438 \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u0438, \u0432\u0456\u0434\u0432\u0456\u0434\u0430\u0439\u0442\u0435 \u043d\u0430\u0448 <a href="https://github.com/kizuna-ai-lab/sokuji">\u0440\u0435\u043f\u043e\u0437\u0438\u0442\u043e\u0440\u0456\u0439 GitHub</a> \u0430\u0431\u043e \u0437\u0432\u2019\u044f\u0436\u0456\u0442\u044c\u0441\u044f \u0437 \u043d\u0430\u043c\u0438 \u0437\u0430 \u0430\u0434\u0440\u0435\u0441\u043e\u044e <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> \u0434\u043b\u044f \u043e\u0442\u0440\u0438\u043c\u0430\u043d\u043d\u044f \u043f\u0456\u0434\u0442\u0440\u0438\u043c\u043a\u0438.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Zoom',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content:
        'Questa guida ti accompagner\u00e0 nella configurazione e nell\u2019uso di Sokuji con Zoom per la traduzione linguistica in tempo reale durante le tue riunioni.',
    },
    steps: [
      {
        title: 'Apri il link della riunione Zoom',
        content:
          'Apri il link della tua riunione Zoom nel browser Chrome o Edge. Il browser tenter\u00e0 di aprire l\u2019applicazione desktop di Zoom.',
        screenshot: '1.png',
        tip: 'Assicurati che l\u2019estensione Sokuji sia installata e attivata nel tuo browser.',
      },
      {
        title: 'Rifiuta l\u2019app nativa',
        content:
          'Quando ti viene chiesto di aprire l\u2019applicazione desktop di Zoom, fai clic su <strong>Annulla</strong> o chiudi la finestra di dialogo per restare nel browser.',
        screenshot: '2.png',
        tip: 'Questo \u00e8 importante \u2013 devi usare la versione browser di Zoom affinch\u00e9 Sokuji funzioni.',
      },
      {
        title: 'Partecipa dal browser',
        content:
          'Dopo aver rifiutato l\u2019app desktop, cerca il link <strong>Partecipa dal tuo browser</strong> che appare nella pagina e fai clic su di esso.',
        screenshot: '3.png',
        tip: 'Questo link potrebbe apparire nella parte inferiore della pagina o in un messaggio di notifica.',
      },
      {
        title: 'Apri la barra laterale di Sokuji',
        content:
          'Fai clic sull\u2019icona dell\u2019estensione Sokuji nell\u2019angolo in alto a destra del tuo browser, quindi fai clic per aprire il pannello laterale di Sokuji.',
        screenshot: '4.png',
        tip: 'La barra laterale si aprir\u00e0 sul lato destro della finestra del browser.',
      },
      {
        title: 'Configura le impostazioni di Sokuji',
        content:
          'Nella barra laterale di Sokuji, configura le impostazioni di traduzione, incluse le lingue di origine e destinazione, il fornitore di IA e altre preferenze.',
        screenshot: '5.png',
        tip: 'Assicurati di testare il microfono e selezionare il dispositivo di input appropriato.',
      },
      {
        title: 'Seleziona il microfono virtuale in Zoom',
        content:
          'Nelle impostazioni audio di Zoom, fai clic sul menu a tendina del microfono e seleziona <strong>Sokuji Virtual Microphone</strong> come dispositivo di input.',
        screenshot: '6.png',
        tip: 'Puoi accedere alle impostazioni audio facendo clic sulla freccia accanto all\u2019icona del microfono in Zoom.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Parla chiaramente ed evita il rumore di fondo',
        'Fai una breve pausa tra le frasi per una migliore precisione della traduzione',
        'Testa il tuo audio prima di riunioni importanti',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Perch\u00e9 non riesco a vedere Sokuji Virtual Microphone?',
          answer:
            'Assicurati che l\u2019estensione Sokuji sia installata e attiva. Prova ad aggiornare la pagina di Zoom. Se il problema persiste, verifica di aver concesso i permessi del microfono all\u2019estensione.',
        },
        {
          question: 'Gli altri partecipanti possono sentire entrambe le lingue?',
          answer:
            'Per impostazione predefinita, i partecipanti sentono solo l’audio tradotto. Se attivi “Passthrough” nel blocco “Voce” di Sokuji, i partecipanti sentiranno anche la tua voce originale sotto la traduzione, nello stesso momento. Questa funzione è utile quando vuoi che gli ascoltatori sentano entrambe le lingue per contesto o verifica.',
        },
        {
          question: 'Sokuji funziona con le registrazioni di Zoom?',
          answer:
            'S\u00ec, l\u2019audio tradotto verr\u00e0 incluso nella registrazione della riunione se stai usando Sokuji Virtual Microphone.',
        },
        {
          question: 'Cos’è il Passthrough?',
          answer:
            'Il Passthrough mescola la tua voce, a un volume più basso, sotto la traduzione nel microfono virtuale, così gli ascoltatori sentono entrambe le lingue insieme. È una sottoriga di “Traduzione che sente l\'altra parte” nel blocco “Voce” (impostazioni “Rapido”, oppure Impostazioni → Avanzato → Audio), con un cursore del volume: 20% per impostazione predefinita, al massimo 60%. Resta in grigio finché “Traduzione che sente l\'altra parte” è disattivata. Questo è particolarmente utile per un pubblico bilingue o quando vuoi mantenere il tono emotivo e le sfumature del tuo discorso originale.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content:
        'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\u2019indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere supporto.',
    },
  },
};

export function ZoomTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/zoom" />;
}
